import React, { useState, useEffect, useRef } from "react";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	IconButton,
	TextField,
	Box,
	Typography,
	Chip,
	Tooltip,
	CircularProgress,
	Switch,
	FormControlLabel,
	Select,
	MenuItem,
	FormControl,
	InputLabel,
	Divider,
	Collapse,
	Paper,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import CloseIcon from "@mui/icons-material/Close";
import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format, parseISO, isValid } from "date-fns";
import {
	CraftModal,
	CraftModalHeader,
	CraftModalBody,
	CraftModalFooter,
	CraftButton,
	CraftSpinner,
} from "@craft-design-system/core-components-react";
import { Plus } from "lucide-react";
import { apiClient } from "../../services/api";

const ADMIN_EMAIL = "yash.sakhare@man.eu";

const CATEGORY_CONFIG = {
	Feature: { emoji: "✨", color: "#d97706", bg: "#fffbeb" },
	"Bug Fix": { emoji: "🐛", color: "#ef4444", bg: "#fff5f5" },
	Improvement: { emoji: "⚡", color: "#3b82f6", bg: "#eff6ff" },
	Security: { emoji: "🔒", color: "#8b5cf6", bg: "#f5f3ff" },
};

const PRIORITY_COLORS = {
	High: "#cc0000",
	Normal: "#1e2832",
	Low: "#888",
};

const EMPTY_FEATURE = () => ({
	id: `f_${Date.now()}_${Math.random()}`,
	title: "",
	description: "",
	category: "Feature",
});
const EMPTY_FORM = () => ({
	title: "",
	version: "",
	date: new Date(),
	summary: "",
	priority: "Normal",
	visible: true,
	features: [EMPTY_FEATURE()],
});

const fmtDate = (dateStr) => {
	try {
		const d = typeof dateStr === "string" ? parseISO(dateStr) : dateStr;
		return isValid(d) ? format(d, "EEE, MMMM dd, yyyy") : String(dateStr);
	} catch {
		return String(dateStr);
	}
};

const fmtMonth = (dateStr) => {
	try {
		const d = typeof dateStr === "string" ? parseISO(dateStr) : dateStr;
		return isValid(d) ? format(d, "MMMM yyyy") : String(dateStr);
	} catch {
		return String(dateStr);
	}
};

const groupByMonth = (releases) => {
	const groups = {};
	releases.forEach((r) => {
		const key = fmtMonth(r.date);
		if (!groups[key]) groups[key] = [];
		groups[key].push(r);
	});
	return groups;
};

// ─── ReleaseQuickViewDialog ────────────────────────────────────────────────────
const ReleaseQuickViewDialog = ({
	release,
	open,
	onClose,
	onOpenAllReleases,
}) => {
	if (!release) return null;
	const firstCategory = release.features?.[0]?.category || "Feature";
	const catConfig =
		CATEGORY_CONFIG[firstCategory] || CATEGORY_CONFIG["Feature"];
	const priorityColor =
		PRIORITY_COLORS[release.priority] || PRIORITY_COLORS["Normal"];

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="sm"
			fullWidth
			PaperProps={{ sx: { borderRadius: "0px" } }}
		>
			<DialogTitle sx={{ pb: 1 }}>
				<Box
					sx={{
						display: "flex",
						alignItems: "flex-start",
						justifyContent: "space-between",
						gap: 1,
					}}
				>
					<Box sx={{ flex: 1, minWidth: 0 }}>
						<Box
							sx={{
								display: "flex",
								alignItems: "center",
								gap: 0.75,
								flexWrap: "wrap",
								mb: 0.75,
							}}
						>
							<Chip
								label={`${catConfig.emoji} ${firstCategory}`}
								size="small"
								sx={{
									backgroundColor: catConfig.bg,
									color: catConfig.color,
									fontWeight: 700,
									fontSize: "0.7rem",
								}}
							/>
							<Chip
								label={release.priority}
								size="small"
								sx={{
									backgroundColor: priorityColor,
									color: "white",
									fontWeight: 700,
									fontSize: "0.68rem",
								}}
							/>
							<Chip
								label={release.version}
								size="small"
								sx={{
									backgroundColor: "#e8f0fe",
									color: "#1a73e8",
									fontWeight: 700,
									fontSize: "0.7rem",
								}}
							/>
						</Box>
						<Typography
							sx={{
								fontWeight: 700,
								fontSize: "1.1rem",
								fontFamily: "MAN EUROPE_Bold, sans-serif",
								color: "#1e2832",
								lineHeight: 1.3,
							}}
						>
							{release.title}
						</Typography>
						<Typography
							variant="caption"
							sx={{ color: "#aaa", mt: 0.25, display: "block" }}
						>
							{fmtDate(release.date)}
						</Typography>
					</Box>
					<IconButton
						onClick={onClose}
						size="small"
						sx={{ mt: -0.5, flexShrink: 0 }}
					>
						<CloseIcon />
					</IconButton>
				</Box>
			</DialogTitle>
			<Divider />
			<DialogContent sx={{ pt: 2 }}>
				{release.summary && (
					<Typography
						variant="body2"
						sx={{ color: "#444", lineHeight: 1.7, mb: 2.5 }}
					>
						{release.summary}
					</Typography>
				)}
				{release.features?.length > 0 && (
					<Box>
						<Typography
							variant="caption"
							sx={{
								fontWeight: 700,
								color: "#999",
								textTransform: "uppercase",
								letterSpacing: "0.07em",
								display: "block",
								mb: 1.5,
							}}
						>
							What's New
						</Typography>
						<Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
							{release.features.map((feat, i) => {
								const fc =
									CATEGORY_CONFIG[feat.category] || CATEGORY_CONFIG["Feature"];
								return (
									<Box
										key={i}
										sx={{
											display: "flex",
											alignItems: "flex-start",
											gap: 1.25,
										}}
									>
										<Chip
											label={`${fc.emoji} ${feat.category}`}
											size="small"
											sx={{
												backgroundColor: fc.bg,
												color: fc.color,
												fontWeight: 700,
												fontSize: "0.65rem",
												flexShrink: 0,
												height: "22px",
												mt: "2px",
											}}
										/>
										<Box>
											<Typography
												variant="body2"
												sx={{
													fontWeight: 600,
													color: "#222",
													lineHeight: 1.35,
												}}
											>
												{feat.title}
											</Typography>
											{feat.description && (
												<Typography
													variant="caption"
													sx={{
														color: "#666",
														lineHeight: 1.55,
														display: "block",
														mt: 0.25,
													}}
												>
													{feat.description}
												</Typography>
											)}
										</Box>
									</Box>
								);
							})}
						</Box>
					</Box>
				)}
			</DialogContent>
			<Divider />
			<DialogActions sx={{ px: 3, py: 2, justifyContent: "space-between" }}>
				<Button
					onClick={onClose}
					variant="outlined"
					color="inherit"
					size="small"
					sx={{ borderRadius: "0px" }}
				>
					Close
				</Button>
				{onOpenAllReleases && (
					<Button
						onClick={() => {
							onClose();
							onOpenAllReleases();
						}}
						variant="contained"
						size="small"
						startIcon={<RocketLaunchIcon />}
						sx={{
							backgroundColor: "#1e2832",
							"&:hover": { backgroundColor: "#2d3e4f" },
							textTransform: "none",
							fontWeight: 600,
							borderRadius: "0px",
						}}
					>
						See all releases
					</Button>
				)}
			</DialogActions>
		</Dialog>
	);
};

// ─── ReleaseCard (ticker) ──────────────────────────────────────────────────────
const ReleaseCard = ({ release, onOpenModal, onOpenRelease }) => {
	const [showTooltip, setShowTooltip] = useState(false);
	const cardRef = useRef(null);

	const firstCategory = release.features?.[0]?.category || "Feature";
	const catConfig =
		CATEGORY_CONFIG[firstCategory] || CATEGORY_CONFIG["Feature"];
	const priorityColor =
		PRIORITY_COLORS[release.priority] || PRIORITY_COLORS["Normal"];
	return (
		<div
			ref={cardRef}
			onMouseEnter={() => setShowTooltip(true)}
			onMouseLeave={() => setShowTooltip(false)}
			style={{
				position: "relative",
				display: "inline-flex",
				flexDirection: "column",
				width: "300px",
				height: "230px",
				flexShrink: 0,
				backgroundColor: catConfig.bg,
				borderRadius: "10px",
				padding: "18px 20px",
				margin: "0 10px",
				boxShadow: "0 2px 8px rgba(0,0,0,0.07)",
				border: `1px solid ${catConfig.color}30`,
				cursor: "pointer",
				transition: "box-shadow 0.2s ease, transform 0.2s ease",
				userSelect: "none",
				overflow: "hidden",
			}}
			onMouseOver={(e) => {
				e.currentTarget.style.boxShadow = "0 8px 24px rgba(0,0,0,0.14)";
				e.currentTarget.style.transform = "translateY(-2px)";
			}}
			onMouseOut={(e) => {
				e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.07)";
				e.currentTarget.style.transform = "translateY(0)";
			}}
			onClick={() => onOpenRelease && onOpenRelease(release)}
		>
			{/* Chips row */}
			<div
				style={{
					display: "flex",
					alignItems: "center",
					gap: "6px",
					marginBottom: "10px",
					flexWrap: "wrap",
				}}
			>
				<span
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: "4px",
						backgroundColor: catConfig.bg,
						color: catConfig.color,
						borderRadius: "20px",
						padding: "3px 10px",
						fontSize: "0.72rem",
						fontWeight: "700",
						fontFamily: "MAN EUROPE_Bold, sans-serif",
					}}
				>
					{catConfig.emoji} {firstCategory}
				</span>
				<span
					style={{
						backgroundColor: priorityColor,
						color: "white",
						borderRadius: "20px",
						padding: "3px 8px",
						fontSize: "0.68rem",
						fontWeight: "700",
						fontFamily: "MAN EUROPE_Bold, sans-serif",
					}}
				>
					{release.priority}
				</span>
				<span
					style={{
						marginLeft: "auto",
						backgroundColor: "#e8f0fe",
						color: "#1a73e8",
						borderRadius: "4px",
						padding: "2px 6px",
						fontSize: "0.68rem",
						fontWeight: "700",
						fontFamily: "MAN EUROPE_Bold, sans-serif",
					}}
				>
					{release.version}
				</span>
			</div>{" "}
			{/* Title */}
			<div
				style={{
					fontSize: "0.88rem",
					fontWeight: "700",
					color: "#1e2832",
					marginBottom: "8px",
					lineHeight: 1.3,
					flexShrink: 0,
					fontFamily: "MAN EUROPE_Bold, sans-serif",
				}}
			>
				{release.title}
			</div>
			{/* Summary — clamps to 2 lines when features present, expands to fill space when no features */}
			{release.summary &&
				(release.features?.length > 0 ? (
					<div
						style={{
							fontSize: "0.76rem",
							color: "#555",
							lineHeight: 1.55,
							flexShrink: 0,
							marginBottom: "6px",
							overflow: "hidden",
							display: "-webkit-box",
							WebkitLineClamp: 2,
							WebkitBoxOrient: "vertical",
							fontFamily: "MAN EUROPE_Regular, sans-serif",
						}}
					>
						{release.summary}
					</div>
				) : (
					<div
						style={{
							fontSize: "0.76rem",
							color: "#555",
							lineHeight: 1.65,
							flex: 1,
							minHeight: 0,
							overflow: "hidden",
							marginBottom: "8px",
							fontFamily: "MAN EUROPE_Regular, sans-serif",
						}}
					>
						{release.summary}
					</div>
				))}
			{/* Features — fills ALL remaining space between summary and date */}
			{release.features?.length > 0 && (
				<div
					style={{
						flex: 1,
						minHeight: 0,
						overflow: "hidden",
						display: "flex",
						flexDirection: "column",
						gap: "5px",
					}}
				>
					{release.features.slice(0, 4).map((feat, i) => {
						const fc =
							CATEGORY_CONFIG[feat.category] || CATEGORY_CONFIG["Feature"];
						return (
							<div
								key={i}
								style={{
									display: "flex",
									alignItems: "flex-start",
									gap: "5px",
									minHeight: 0,
									overflow: "hidden",
								}}
							>
								<span
									style={{
										backgroundColor: fc.bg,
										color: fc.color,
										borderRadius: "4px",
										padding: "1px 5px",
										fontSize: "0.62rem",
										fontWeight: "700",
										flexShrink: 0,
										marginTop: "2px",
										whiteSpace: "nowrap",
										fontFamily: "MAN EUROPE_Regular, sans-serif",
									}}
								>
									{fc.emoji} {feat.category}
								</span>
								<div style={{ minWidth: 0, overflow: "hidden" }}>
									<div
										style={{
											fontSize: "0.75rem",
											fontWeight: "600",
											color: "#333",
											overflow: "hidden",
											textOverflow: "ellipsis",
											whiteSpace: "nowrap",
											fontFamily: "MAN EUROPE_Bold, sans-serif",
										}}
									>
										{feat.title}
									</div>{" "}
									{feat.description && (
										<div
											style={{
												fontSize: "0.68rem",
												color: "#777",
												lineHeight: 1.45,
												fontFamily: "MAN EUROPE_Regular, sans-serif",
											}}
										>
											{feat.description}
										</div>
									)}
								</div>
							</div>
						);
					})}
					{release.features.length > 4 && (
						<div
							style={{
								fontSize: "0.67rem",
								color: "#bbb",
								fontStyle: "italic",
								flexShrink: 0,
								fontFamily: "MAN EUROPE_Bold, sans-serif",
							}}
						>
							+{release.features.length - 4} more…
						</div>
					)}
				</div>
			)}{" "}
			{/* Date — always pinned to bottom */}
			<div
				style={{
					fontSize: "0.71rem",
					color: "#aaa",
					paddingTop: "8px",
					borderTop: `1px solid ${catConfig.color}25`,
					flexShrink: 0,
					fontFamily: "MAN EUROPE_Bold, sans-serif",
				}}
			>
				{fmtDate(release.date)}
			</div>{" "}
			{/* Hover tooltip — full details */}
			{showTooltip && (
				<div
					onMouseEnter={() => setShowTooltip(true)}
					onMouseLeave={() => setShowTooltip(false)}
					style={{
						position: "absolute",
						bottom: "calc(100% + 12px)",
						left: "50%",
						transform: "translateX(-50%)",
						zIndex: 9999,
						backgroundColor: "white",
						border: "1px solid #e0e0e0",
						borderRadius: "14px",
						padding: "20px",
						width: "360px",
						maxHeight: "420px",
						overflowY: "auto",
						boxShadow: "0 16px 48px rgba(0,0,0,0.18)",
						pointerEvents: "auto",
						animation: "tooltipFadeIn 0.15s ease",
					}}
				>
					{/* Arrow */}
					<div
						style={{
							position: "absolute",
							bottom: "-7px",
							left: "50%",
							transform: "translateX(-50%) rotate(45deg)",
							width: "14px",
							height: "14px",
							backgroundColor: "white",
							border: "1px solid #e0e0e0",
							borderTop: "none",
							borderLeft: "none",
						}}
					/>
					{/* Header chips */}
					<div
						style={{
							display: "flex",
							gap: "6px",
							flexWrap: "wrap",
							marginBottom: "10px",
							alignItems: "center",
						}}
					>
						<span
							style={{
								backgroundColor: catConfig.bg,
								color: catConfig.color,
								borderRadius: "20px",
								padding: "3px 10px",
								fontSize: "0.72rem",
								fontWeight: "700",
								fontFamily: "MAN EUROPE_Bold, sans-serif",
							}}
						>
							{catConfig.emoji} {firstCategory}
						</span>
						<span
							style={{
								backgroundColor: priorityColor,
								color: "white",
								borderRadius: "20px",
								padding: "3px 8px",
								fontSize: "0.68rem",
								fontWeight: "700",
								fontFamily: "MAN EUROPE_Bold, sans-serif",
							}}
						>
							{release.priority}
						</span>
						<span
							style={{
								marginLeft: "auto",
								backgroundColor: "#e8f0fe",
								color: "#1a73e8",
								borderRadius: "4px",
								padding: "2px 6px",
								fontSize: "0.68rem",
								fontWeight: "700",
								fontFamily: "MAN EUROPE_Bold, sans-serif",
							}}
						>
							{release.version}
						</span>
					</div>
					{/* Title + date */}
					<div
						style={{
							fontSize: "0.97rem",
							fontWeight: "700",
							color: "#1e2832",
							marginBottom: "2px",
							lineHeight: 1.3,
							fontFamily: "MAN EUROPE_Bold, sans-serif",
						}}
					>
						{release.title}
					</div>
					<div
						style={{
							fontSize: "0.73rem",
							color: "#aaa",
							marginBottom: "12px",
							fontFamily: "MAN EUROPE_Bold, sans-serif",
						}}
					>
						{fmtDate(release.date)}
					</div>
					{/* Full summary */}
					{release.summary && (
						<div
							style={{
								fontSize: "0.82rem",
								color: "#444",
								lineHeight: 1.65,
								marginBottom: "14px",
								paddingBottom: "14px",
								borderBottom: "1px solid #f0f0f0",
								fontFamily: "MAN EUROPE_Regular, sans-serif",
							}}
						>
							{release.summary}
						</div>
					)}
					{/* All features with full descriptions */}
					{release.features?.length > 0 && (
						<div>
							<div
								style={{
									fontSize: "0.72rem",
									fontWeight: "700",
									color: "#999",
									textTransform: "uppercase",
									letterSpacing: "0.06em",
									marginBottom: "10px",
									fontFamily: "MAN EUROPE_Regular, sans-serif",
								}}
							>
								What's New
							</div>
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									gap: "10px",
								}}
							>
								{release.features.map((feat, i) => {
									const fc =
										CATEGORY_CONFIG[feat.category] ||
										CATEGORY_CONFIG["Feature"];
									return (
										<div
											key={i}
											style={{
												display: "flex",
												alignItems: "flex-start",
												gap: "8px",
											}}
										>
											<span
												style={{
													backgroundColor: fc.bg,
													color: fc.color,
													borderRadius: "4px",
													padding: "2px 7px",
													fontSize: "0.65rem",
													fontWeight: "700",
													flexShrink: 0,
													marginTop: "1px",
													whiteSpace: "nowrap",
													fontFamily: "MAN EUROPE_Bold, sans-serif",
												}}
											>
												{fc.emoji} {feat.category}
											</span>
											<div>
												<div
													style={{
														fontSize: "0.81rem",
														fontWeight: "600",
														color: "#222",
														lineHeight: 1.3,
														fontFamily: "MAN EUROPE_Bold, sans-serif",
													}}
												>
													{feat.title}
												</div>
												{feat.description && (
													<div
														style={{
															fontSize: "0.74rem",
															color: "#666",
															lineHeight: 1.5,
															marginTop: "2px",
															fontFamily: "MAN EUROPE_Bold, sans-serif",
														}}
													>
														{feat.description}
													</div>
												)}
											</div>
										</div>
									);
								})}
							</div>
						</div>
					)}{" "}
					{/* See all / View details buttons */}
					<div
						style={{
							marginTop: "16px",
							borderTop: "1px solid #f0f0f0",
							paddingTop: "12px",
							display: "flex",
							flexDirection: "column",
							gap: "8px",
						}}
					>
						{onOpenRelease && (
							<button
								onClick={(e) => {
									e.stopPropagation();
									onOpenRelease(release);
								}}
								style={{
									width: "100%",
									padding: "8px",
									backgroundColor: "#1e2832",
									color: "white",
									border: "none",
									borderRadius: "6px",
									fontSize: "0.8rem",
									fontWeight: "700",
									fontFamily: "MAN EUROPE_Bold, sans-serif",
									cursor: "pointer",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: "6px",
								}}
								onMouseEnter={(e) =>
									(e.currentTarget.style.backgroundColor = "#2d3e4f")
								}
								onMouseLeave={(e) =>
									(e.currentTarget.style.backgroundColor = "#1e2832")
								}
							>
								🔍 View details
							</button>
						)}
						{onOpenModal && (
							<button
								onClick={(e) => {
									e.stopPropagation();
									onOpenModal();
								}}
								style={{
									width: "100%",
									padding: "7px",
									backgroundColor: "transparent",
									color: "#1e2832",
									border: "1px solid #1e2832",
									borderRadius: "6px",
									fontSize: "0.78rem",
									fontWeight: "700",
									fontFamily: "MAN EUROPE_Bold, sans-serif",
									cursor: "pointer",
									display: "flex",
									alignItems: "center",
									justifyContent: "center",
									gap: "6px",
								}}
								onMouseEnter={(e) => {
									e.currentTarget.style.backgroundColor = "#f5f5f5";
								}}
								onMouseLeave={(e) => {
									e.currentTarget.style.backgroundColor = "transparent";
								}}
							>
								🚀 See all releases
							</button>
						)}
					</div>
				</div>
			)}
		</div>
	);
};

// ─── NewReleasesTickerSection ──────────────────────────────────────────────────
const NewReleasesTickerSection = ({ onOpenModal }) => {
	const [releases, setReleases] = useState([]);
	const [loading, setLoading] = useState(true);
	const [isPaused, setIsPaused] = useState(false);
	const [selectedRelease, setSelectedRelease] = useState(null);
	const tickerRef = useRef(null);
	const animRef = useRef(null);
	const posRef = useRef(0);
	const lastTimeRef = useRef(null);
	const isDragging = useRef(false);
	const dragStartX = useRef(0);
	const dragStartPos = useRef(0);

	useEffect(() => {
		apiClient
			.get("/api/releases")
			.then((r) => setReleases(r.data || []))
			.catch(() => setReleases([]))
			.finally(() => setLoading(false));
	}, []);

	useEffect(() => {
		if (!releases.length || !tickerRef.current) return;

		const SPEED = 40; // px per second
		const inner = tickerRef.current;

		const tick = (timestamp) => {
			if (!lastTimeRef.current) lastTimeRef.current = timestamp;
			const delta = timestamp - lastTimeRef.current;
			lastTimeRef.current = timestamp;

			if (!isPaused && !isDragging.current) {
				posRef.current += (SPEED * delta) / 1000;
				const totalWidth = inner.scrollWidth / 2;
				if (posRef.current >= totalWidth) posRef.current = 0;
				inner.style.transform = `translateX(-${posRef.current}px)`;
			} else {
				lastTimeRef.current = timestamp;
			}

			animRef.current = requestAnimationFrame(tick);
		};

		animRef.current = requestAnimationFrame(tick);
		return () => {
			if (animRef.current) cancelAnimationFrame(animRef.current);
		};
	}, [releases, isPaused]);

	const handleDragStart = (e) => {
		isDragging.current = true;
		dragStartX.current =
			e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
		dragStartPos.current = posRef.current;
	};

	const handleDragMove = (e) => {
		if (!isDragging.current || !tickerRef.current) return;
		const clientX = e.type === "touchmove" ? e.touches[0].clientX : e.clientX;
		const delta = dragStartX.current - clientX;
		const totalWidth = tickerRef.current.scrollWidth / 2;
		let newPos = dragStartPos.current + delta;
		// Wrap around
		if (newPos < 0) newPos += totalWidth;
		if (newPos >= totalWidth) newPos -= totalWidth;
		posRef.current = newPos;
		tickerRef.current.style.transform = `translateX(-${posRef.current}px)`;
		e.preventDefault();
	};
	const handleDragEnd = () => {
		isDragging.current = false;
	};

	const handleWheel = (e) => {
		if (!tickerRef.current) return;
		e.preventDefault();
		const totalWidth = tickerRef.current.scrollWidth / 2;
		let newPos = posRef.current + e.deltaX + e.deltaY;
		if (newPos < 0) newPos += totalWidth;
		if (newPos >= totalWidth) newPos -= totalWidth;
		posRef.current = newPos;
		tickerRef.current.style.transform = `translateX(-${posRef.current}px)`;
	};

	const scrollBy = (amount) => {
		if (!tickerRef.current) return;
		const totalWidth = tickerRef.current.scrollWidth / 2;
		let newPos = posRef.current + amount;
		if (newPos < 0) newPos += totalWidth;
		if (newPos >= totalWidth) newPos -= totalWidth;
		posRef.current = newPos;
		tickerRef.current.style.transform = `translateX(-${posRef.current}px)`;
	};

	// Smooth arrow scroll — animate over ~300 ms
	const scrollArrow = (direction) => {
		const STEP = 320; // px per arrow click
		const DURATION = 300;
		const start = posRef.current;
		const target = start + direction * STEP;
		const startTime = performance.now();
		const easeOut = (t) => 1 - Math.pow(1 - t, 3);
		const animate = (now) => {
			const elapsed = now - startTime;
			const progress = Math.min(elapsed / DURATION, 1);
			const totalWidth = tickerRef.current
				? tickerRef.current.scrollWidth / 2
				: 1;
			let pos = start + (target - start) * easeOut(progress);
			if (pos < 0) pos += totalWidth;
			if (pos >= totalWidth) pos -= totalWidth;
			posRef.current = pos;
			if (tickerRef.current)
				tickerRef.current.style.transform = `translateX(-${pos}px)`;
			if (progress < 1) requestAnimationFrame(animate);
		};
		requestAnimationFrame(animate);
	};

	if (loading)
		return (
			<div
				style={{
					margin: "0 32px 32px",
					textAlign: "center",
					padding: "40px",
					color: "#aaa",
				}}
			>
				Loading releases…
			</div>
		);
	if (!releases.length) return null;

	const allCards = [...releases, ...releases];

	// Arrow button style
	const arrowBtn = (side) => ({
		position: "absolute",
		top: "50%",
		[side]: "6px",
		transform: "translateY(-50%)",
		zIndex: 10,
		width: "32px",
		height: "32px",
		borderRadius: "50%",
		border: "none",
		backgroundColor: "white",
		boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
		cursor: "pointer",
		display: "flex",
		alignItems: "center",
		justifyContent: "center",
		fontSize: "1rem",
		color: "#1e2832",
		transition: "box-shadow 0.15s, background 0.15s",
		flexShrink: 0,
	});

	return (
		<div style={{ margin: "0 32px 32px" }}>
			<style>{`@keyframes tooltipFadeIn { from { opacity:0; transform:translateX(-50%) translateY(6px); } to { opacity:1; transform:translateX(-50%) translateY(0); } }`}</style>
			<div
				style={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					marginBottom: "16px",
				}}
			>
				<h2
					style={{
						fontSize: "1.1rem",
						fontWeight: "700",
						color: "#1e2832",
						margin: 0,
						fontFamily: "MAN EUROPE_Bold, sans-serif",
					}}
				>
					New Releases
				</h2>
				<button
					onClick={() => onOpenModal && onOpenModal()}
					style={{
						display: "inline-flex",
						alignItems: "center",
						gap: "4px",
						backgroundColor: "transparent",
						color: "#cc0000",
						border: "none",
						fontSize: "0.85rem",
						fontWeight: "600",
						cursor: "pointer",
						fontFamily: "MAN EUROPE_Bold, sans-serif",
						padding: "4px 0",
					}}
				>
					See all &rsaquo;
				</button>
			</div>
			<div style={{ position: "relative" }}>
				{/* Left arrow */}
				<button
					style={arrowBtn("left")}
					onClick={() => scrollArrow(-1)}
					onMouseEnter={(e) => {
						e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.22)";
						e.currentTarget.style.backgroundColor = "#f5f5f5";
					}}
					onMouseLeave={(e) => {
						e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
						e.currentTarget.style.backgroundColor = "white";
					}}
				>
					‹
				</button>

				{/* Right arrow */}
				<button
					style={arrowBtn("right")}
					onClick={() => scrollArrow(1)}
					onMouseEnter={(e) => {
						e.currentTarget.style.boxShadow = "0 4px 16px rgba(0,0,0,0.22)";
						e.currentTarget.style.backgroundColor = "#f5f5f5";
					}}
					onMouseLeave={(e) => {
						e.currentTarget.style.boxShadow = "0 2px 8px rgba(0,0,0,0.15)";
						e.currentTarget.style.backgroundColor = "white";
					}}
				>
					›
				</button>

				<div
					style={{
						overflow: "hidden",
						position: "relative",
						paddingBottom: "8px",
						cursor: isDragging.current ? "grabbing" : "grab",
						WebkitMaskImage:
							"linear-gradient(to right, transparent 0%, black 60px, black calc(100% - 60px), transparent 100%)",
						maskImage:
							"linear-gradient(to right, transparent 0%, black 60px, black calc(100% - 60px), transparent 100%)",
					}}
					onMouseEnter={() => setIsPaused(true)}
					onMouseLeave={() => {
						setIsPaused(false);
						handleDragEnd();
					}}
					onMouseDown={handleDragStart}
					onMouseMove={handleDragMove}
					onMouseUp={handleDragEnd}
					onWheel={handleWheel}
					onTouchStart={handleDragStart}
					onTouchMove={handleDragMove}
					onTouchEnd={handleDragEnd}
				>
					<div
						ref={tickerRef}
						style={{
							display: "inline-flex",
							alignItems: "stretch",
							willChange: "transform",
							userSelect: "none",
						}}
					>
						{allCards.map((release, index) => (
							<ReleaseCard
								key={`${release.id}-${index}`}
								release={release}
								onOpenModal={onOpenModal}
								onOpenRelease={(r) => {
									if (!isDragging.current) setSelectedRelease(r);
								}}
							/>
						))}
					</div>
				</div>
			</div>

			<ReleaseQuickViewDialog
				release={selectedRelease}
				open={!!selectedRelease}
				onClose={() => setSelectedRelease(null)}
				onOpenAllReleases={onOpenModal}
			/>
		</div>
	);
};

// ─── FeatureRow ────────────────────────────────────────────────────────────────
const FeatureRow = ({ feature, onChange, onDelete, canDelete }) => (
	<Box sx={{ display: "flex", gap: 1, alignItems: "flex-start", mb: 1 }}>
		<FormControl size="small" sx={{ minWidth: 130, flexShrink: 0 }}>
			<InputLabel>Category</InputLabel>
			<Select
				value={feature.category}
				label="Category"
				onChange={(e) => onChange({ ...feature, category: e.target.value })}
			>
				{Object.keys(CATEGORY_CONFIG).map((c) => (
					<MenuItem key={c} value={c}>
						{CATEGORY_CONFIG[c].emoji} {c}
					</MenuItem>
				))}
			</Select>
		</FormControl>
		<TextField
			size="small"
			label="Title"
			value={feature.title}
			onChange={(e) => onChange({ ...feature, title: e.target.value })}
			sx={{ flex: 1 }}
			required
		/>
		<TextField
			size="small"
			label="Description (optional)"
			value={feature.description}
			onChange={(e) => onChange({ ...feature, description: e.target.value })}
			sx={{ flex: 1.5 }}
		/>
		<IconButton
			size="small"
			onClick={onDelete}
			disabled={!canDelete}
			color="error"
			sx={{ mt: 0.5 }}
		>
			<DeleteIcon fontSize="small" />
		</IconButton>
	</Box>
);

// ─── ReleaseFormDialog ─────────────────────────────────────────────────────────
const ReleaseFormDialog = ({ open, onClose, onSave, initialData, loading }) => {
	const [form, setForm] = useState(EMPTY_FORM());
	const [errors, setErrors] = useState({});

	useEffect(() => {
		if (open) {
			if (initialData) {
				setForm({
					title: initialData.title || "",
					version: initialData.version || "",
					date: initialData.date
						? typeof initialData.date === "string"
							? parseISO(initialData.date)
							: initialData.date
						: new Date(),
					summary: initialData.summary || "",
					priority: initialData.priority || "Normal",
					visible:
						initialData.visible !== undefined ? initialData.visible : true,
					features: initialData.features?.length
						? initialData.features.map((f) => ({
								...f,
								id: f.id || `f_${Date.now()}_${Math.random()}`,
							}))
						: [EMPTY_FEATURE()],
				});
			} else {
				setForm(EMPTY_FORM());
			}
			setErrors({});
		}
	}, [open, initialData]);

	const validate = () => {
		const e = {};
		if (!form.title.trim()) e.title = "Title is required";
		if (!form.version.trim()) e.version = "Version is required";
		if (!form.date || !isValid(form.date)) e.date = "Valid date is required";
		form.features.forEach((f, i) => {
			if (!f.title.trim()) e[`feat_${i}`] = true;
		});
		setErrors(e);
		return Object.keys(e).length === 0;
	};

	const handleSave = () => {
		if (!validate()) return;
		onSave({
			title: form.title.trim(),
			version: form.version.trim(),
			date: form.date.toISOString(),
			summary: form.summary.trim(),
			priority: form.priority,
			visible: form.visible,
			features: form.features
				.filter((f) => f.title.trim())
				.map(({ id, ...rest }) => ({ ...rest })),
		});
	};

	const updateFeature = (idx, updated) =>
		setForm((f) => {
			const feats = [...f.features];
			feats[idx] = updated;
			return { ...f, features: feats };
		});
	const deleteFeature = (idx) =>
		setForm((f) => ({
			...f,
			features: f.features.filter((_, i) => i !== idx),
		}));
	const addFeature = () =>
		setForm((f) => ({ ...f, features: [...f.features, EMPTY_FEATURE()] }));

	return (
		<Dialog
			open={open}
			onClose={onClose}
			maxWidth="md"
			fullWidth
			PaperProps={{ sx: { borderRadius: "12px" } }}
		>
			<DialogTitle
				sx={{
					display: "flex",
					alignItems: "center",
					justifyContent: "space-between",
					pb: 1,
					fontFamily: "MAN EUROPE_Bold, sans-serif",
					fontWeight: 700,
				}}
			>
				{initialData ? "✏️ Edit Release" : "🚀 New Release"}
				<IconButton onClick={onClose} size="small">
					<CloseIcon />
				</IconButton>
			</DialogTitle>
			<Divider />
			<DialogContent sx={{ pt: 2 }}>
				<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
					<TextField
						label="Release Title"
						value={form.title}
						onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
						error={!!errors.title}
						helperText={errors.title}
						fullWidth
						required
					/>
					<Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
						<TextField
							label="Version"
							value={form.version}
							onChange={(e) =>
								setForm((f) => ({ ...f, version: e.target.value }))
							}
							error={!!errors.version}
							helperText={errors.version}
							sx={{ flex: 1, minWidth: "120px" }}
							required
							placeholder="e.g. v2.4.1"
						/>
						<LocalizationProvider dateAdapter={AdapterDateFns}>
							<DatePicker
								label="Release Date"
								value={form.date}
								onChange={(d) => setForm((f) => ({ ...f, date: d }))}
								slotProps={{
									textField: {
										error: !!errors.date,
										helperText: errors.date,
										sx: { flex: 1, minWidth: "160px" },
									},
								}}
							/>
						</LocalizationProvider>
						<FormControl sx={{ flex: 1, minWidth: "120px" }}>
							<InputLabel>Priority</InputLabel>
							<Select
								value={form.priority}
								label="Priority"
								onChange={(e) =>
									setForm((f) => ({ ...f, priority: e.target.value }))
								}
							>
								{Object.keys(PRIORITY_COLORS).map((p) => (
									<MenuItem key={p} value={p}>
										{p}
									</MenuItem>
								))}
							</Select>
						</FormControl>
					</Box>
					<TextField
						label="Summary"
						value={form.summary}
						onChange={(e) =>
							setForm((f) => ({ ...f, summary: e.target.value }))
						}
						fullWidth
						multiline
						rows={2}
						placeholder="Brief description of this release…"
					/>
					<FormControlLabel
						control={
							<Switch
								checked={form.visible}
								onChange={(e) =>
									setForm((f) => ({ ...f, visible: e.target.checked }))
								}
								color="primary"
							/>
						}
						label={
							<Typography variant="body2">
								{form.visible ? "Visible to all users" : "Hidden from users"}
							</Typography>
						}
					/>
					<Box>
						<Typography
							variant="subtitle2"
							sx={{ fontWeight: 700, mb: 1, color: "#1e2832" }}
						>
							Features / Changes
						</Typography>
						{form.features.map((feat, i) => (
							<FeatureRow
								key={feat.id}
								feature={feat}
								onChange={(updated) => updateFeature(i, updated)}
								onDelete={() => deleteFeature(i)}
								canDelete={form.features.length > 1}
							/>
						))}
						{form.features.some((_, i) => errors[`feat_${i}`]) && (
							<Typography variant="caption" color="error">
								All feature titles are required.
							</Typography>
						)}
						<Button
							size="small"
							startIcon={<AddIcon />}
							onClick={addFeature}
							sx={{ mt: 0.5 }}
						>
							Add feature
						</Button>
					</Box>
				</Box>
			</DialogContent>
			<Divider />
			<DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
				<Button
					onClick={onClose}
					variant="outlined"
					color="inherit"
					size="small"
					sx={{ borderRadius: "0px" }}
				>
					Close
				</Button>
				<Button
					onClick={handleSave}
					variant="contained"
					disabled={loading}
					sx={{
						backgroundColor: "#1e2832",
						"&:hover": { backgroundColor: "#2d3e4f" },
					}}
				>
					{loading ? (
						<CircularProgress size={18} color="inherit" />
					) : initialData ? (
						"Save Changes"
					) : (
						"Create Release"
					)}
				</Button>
			</DialogActions>
		</Dialog>
	);
};

// ─── ReleaseDetailCard ─────────────────────────────────────────────────────────
const ReleaseDetailCard = ({ release, isAdmin, onEdit, onDelete }) => {
	const [expanded, setExpanded] = useState(false);
	const firstCategory = release.features?.[0]?.category || "Feature";
	const catConfig =
		CATEGORY_CONFIG[firstCategory] || CATEGORY_CONFIG["Feature"];
	const priorityColor =
		PRIORITY_COLORS[release.priority] || PRIORITY_COLORS["Normal"];

	return (
		<Paper
			elevation={0}
			sx={{
				border: "1px solid #eee",
				borderRadius: "10px",
				mb: 2,
				overflow: "hidden",
				opacity: release.visible ? 1 : 0.6,
			}}
		>
			<Box
				sx={{
					display: "flex",
					alignItems: "center",
					gap: 1,
					p: "14px 16px",
					cursor: "pointer",
					"&:hover": { backgroundColor: "#fafafa" },
				}}
				onClick={() => setExpanded((v) => !v)}
			>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1,
							flexWrap: "wrap",
							mb: 0.5,
						}}
					>
						<Chip
							label={`${catConfig.emoji} ${firstCategory}`}
							size="small"
							sx={{
								backgroundColor: catConfig.bg,
								color: catConfig.color,
								fontWeight: 700,
								fontSize: "0.7rem",
							}}
						/>
						<Chip
							label={release.priority}
							size="small"
							sx={{
								backgroundColor: priorityColor,
								color: "white",
								fontWeight: 700,
								fontSize: "0.68rem",
							}}
						/>
						<Chip
							label={release.version}
							size="small"
							sx={{
								backgroundColor: "#e8f0fe",
								color: "#1a73e8",
								fontWeight: 700,
								fontSize: "0.68rem",
							}}
						/>
						{!release.visible && (
							<Chip
								icon={
									<VisibilityOffIcon sx={{ fontSize: "14px !important" }} />
								}
								label="Hidden"
								size="small"
								sx={{ fontSize: "0.68rem" }}
							/>
						)}
					</Box>
					<Typography
						sx={{
							fontWeight: 700,
							fontSize: "0.92rem",
							color: "#1e2832",
							lineHeight: 1.3,
						}}
					>
						{release.title}
					</Typography>
					<Typography variant="caption" sx={{ color: "#aaa" }}>
						{fmtDate(release.date)}
					</Typography>
				</Box>
				<Box
					sx={{
						display: "flex",
						alignItems: "center",
						gap: 0.5,
						flexShrink: 0,
					}}
				>
					{isAdmin && (
						<>
							<Tooltip title="Edit">
								<IconButton
									size="small"
									onClick={(e) => {
										e.stopPropagation();
										onEdit(release);
									}}
								>
									<EditIcon fontSize="small" />
								</IconButton>
							</Tooltip>
							<Tooltip title="Delete">
								<IconButton
									size="small"
									color="error"
									onClick={(e) => {
										e.stopPropagation();
										onDelete(release);
									}}
								>
									<DeleteIcon fontSize="small" />
								</IconButton>
							</Tooltip>
						</>
					)}
					{expanded ? (
						<ExpandLessIcon sx={{ color: "#aaa" }} />
					) : (
						<ExpandMoreIcon sx={{ color: "#aaa" }} />
					)}
				</Box>
			</Box>
			<Collapse in={expanded}>
				<Divider />
				<Box sx={{ p: "14px 16px", backgroundColor: "#fafafa" }}>
					{release.summary && (
						<Typography
							variant="body2"
							sx={{ color: "#555", mb: 2, lineHeight: 1.6 }}
						>
							{release.summary}
						</Typography>
					)}
					{release.features?.length > 0 && (
						<Box>
							<Typography
								variant="caption"
								sx={{
									fontWeight: 700,
									color: "#888",
									textTransform: "uppercase",
									letterSpacing: "0.05em",
									display: "block",
									mb: 1,
								}}
							>
								What's New
							</Typography>
							<Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
								{release.features.map((feat, i) => {
									const fc =
										CATEGORY_CONFIG[feat.category] ||
										CATEGORY_CONFIG["Feature"];
									return (
										<Box
											key={i}
											sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}
										>
											<Chip
												label={`${fc.emoji} ${feat.category}`}
												size="small"
												sx={{
													backgroundColor: fc.bg,
													color: fc.color,
													fontWeight: 700,
													fontSize: "0.65rem",
													flexShrink: 0,
													height: "20px",
												}}
											/>
											<Box>
												<Typography
													variant="body2"
													sx={{
														fontWeight: 600,
														color: "#333",
														lineHeight: 1.3,
													}}
												>
													{feat.title}
												</Typography>
												{feat.description && (
													<Typography
														variant="caption"
														sx={{
															color: "#888",
															lineHeight: 1.4,
															display: "block",
														}}
													>
														{feat.description}
													</Typography>
												)}
											</Box>
										</Box>
									);
								})}
							</Box>
						</Box>
					)}
					{isAdmin && (
						<Typography
							variant="caption"
							sx={{ color: "#bbb", display: "block", mt: 1.5 }}
						>
							Created by {release.created_by}
							{release.updated_by ? ` · Updated by ${release.updated_by}` : ""}
						</Typography>
					)}
				</Box>
			</Collapse>
		</Paper>
	);
};

// ─── NewReleasesModal ──────────────────────────────────────────────────────────
const NewReleasesModal = ({ open, onClose, currentUser }) => {
	const isAdmin =
		!!currentUser && currentUser.toLowerCase() === ADMIN_EMAIL.toLowerCase();

	const [releases, setReleases] = useState([]);
	const [loading, setLoading] = useState(false);
	const [actionLoading, setActionLoading] = useState(false);
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);
	const [formOpen, setFormOpen] = useState(false);
	const [editingRelease, setEditingRelease] = useState(null);
	const [deleteTarget, setDeleteTarget] = useState(null);
	const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

	const fetchReleases = async () => {
		setLoading(true);
		setError(null);
		try {
			const endpoint = isAdmin
				? `/api/releases/admin?user=${encodeURIComponent(currentUser)}`
				: "/api/releases";
			const response = await apiClient.get(endpoint);
			setReleases(response.data || []);
		} catch (err) {
			setError(err.response?.data?.detail || "Failed to load releases");
		} finally {
			setLoading(false);
		}
	};

	useEffect(() => {
		if (open) fetchReleases();
	}, [open]); // eslint-disable-line react-hooks/exhaustive-deps

	const handleSave = async (formData) => {
		setActionLoading(true);
		setError(null);
		try {
			const payload = { ...formData, user: currentUser };
			if (editingRelease) {
				await apiClient.put(`/api/releases/${editingRelease.id}`, payload);
				setSuccess("Release updated successfully.");
			} else {
				await apiClient.post("/api/releases", payload);
				setSuccess("Release created successfully.");
			}
			setFormOpen(false);
			setEditingRelease(null);
			await fetchReleases();
		} catch (err) {
			setError(err.response?.data?.detail || "Failed to save release");
		} finally {
			setActionLoading(false);
		}
	};

	const confirmDelete = (release) => {
		setDeleteTarget(release);
		setDeleteConfirmOpen(true);
	};

	const handleDelete = async () => {
		if (!deleteTarget) return;
		setActionLoading(true);
		setError(null);
		try {
			await apiClient.delete(`/api/releases/${deleteTarget.id}`, {
				data: { user: currentUser },
			});
			setSuccess("Release deleted.");
			setDeleteConfirmOpen(false);
			setDeleteTarget(null);
			await fetchReleases();
		} catch (err) {
			setError(err.response?.data?.detail || "Failed to delete release");
		} finally {
			setActionLoading(false);
		}
	};

	const groups = groupByMonth(releases);

	const rn = {
		// Constrained shell: body scrolls internally so footer stays visible.
		shell: {
			width: "660px",
			maxWidth: "100%",
			display: "flex",
			flexDirection: "column",
			maxHeight: "68vh",
		},
		toolbar: {
			display: "flex",
			justifyContent: "flex-end",
			marginBottom: "12px",
			flexShrink: 0,
		},
		addBtn: {
			background: "none",
			border: "1px solid #1e2832",
			borderRadius: "4px",
			padding: "5px 12px",
			cursor: "pointer",
			display: "flex",
			alignItems: "center",
			gap: "5px",
			fontSize: "12px",
			fontFamily: "MAN EUROPE_Bold, Arial, sans-serif",
			color: "#1e2832",
		},
		alert: {
			padding: "10px 14px",
			borderRadius: "6px",
			marginBottom: "12px",
			fontSize: "13px",
			backgroundColor: "#fde8e8",
			border: "1px solid #f5c6c6",
			color: "#cc0000",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			flexShrink: 0,
		},
		successAlert: {
			padding: "10px 14px",
			borderRadius: "6px",
			marginBottom: "12px",
			fontSize: "13px",
			backgroundColor: "#e8f5e9",
			border: "1px solid #b7dfb9",
			color: "#2e7d32",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
			flexShrink: 0,
		},
		scrollArea: {
			flex: 1,
			minHeight: 0,
			overflowY: "auto",
			paddingRight: "2px",
		},
		empty: {
			textAlign: "center",
			padding: "48px 20px",
			color: "#aaa",
			fontFamily: "MAN EUROPE_Regular, Arial, sans-serif",
		},
		loadingWrap: {
			display: "flex",
			justifyContent: "center",
			alignItems: "center",
			padding: "40px",
			gap: "12px",
		},
	};

	return (
		<>
			{/* Hidden while the MUI form / delete dialogs are open: the Craft
			    modal is a native top-layer <dialog>, so MUI dialogs would render
			    behind it. Hiding it lets those dialogs take over, then it returns. */}
			<CraftModal
				show={open && !formOpen && !deleteConfirmOpen}
				allowClickOut={false}
				onCraftClose={onClose}
			>
				<CraftModalHeader
					header="Release Notes"
					content="Product updates — features, fixes and improvements."
				/>
				<CraftModalBody>
					<div style={rn.shell}>
						{isAdmin && (
							<div style={rn.toolbar}>
								<button
									type="button"
									style={rn.addBtn}
									onClick={() => {
										setEditingRelease(null);
										setFormOpen(true);
									}}
								>
									<Plus size={13} /> New Release
								</button>
							</div>
						)}

						{error && <div style={rn.alert}>⚠️ {error}</div>}
						{success && <div style={rn.successAlert}>✓ {success}</div>}

						{loading && (
							<div style={rn.loadingWrap}>
								<CraftSpinner size="m" />
								<span style={{ color: "#666" }}>Loading releases…</span>
							</div>
						)}

						{!loading && releases.length === 0 && (
							<div style={rn.empty}>
								<RocketLaunchIcon sx={{ fontSize: 48, mb: 1, opacity: 0.3 }} />
								<Typography>No releases yet.</Typography>
							</div>
						)}

						{!loading && releases.length > 0 && (
							<div style={rn.scrollArea}>
								{Object.entries(groups).map(([month, group]) => (
									<Box key={month} sx={{ mb: 3 }}>
										<Typography
											sx={{
												fontWeight: 700,
												fontSize: "0.78rem",
												color: "#999",
												textTransform: "uppercase",
												letterSpacing: "0.08em",
												mb: 1.5,
												display: "flex",
												alignItems: "center",
												gap: 1.5,
											}}
										>
											<span
												style={{
													flex: 1,
													height: "1px",
													backgroundColor: "#ebebeb",
													display: "inline-block",
												}}
											/>
											{month}
											<span
												style={{
													flex: 1,
													height: "1px",
													backgroundColor: "#ebebeb",
													display: "inline-block",
												}}
											/>
										</Typography>
										{group.map((release) => (
											<ReleaseDetailCard
												key={release.id}
												release={release}
												isAdmin={isAdmin}
												onEdit={(r) => {
													setEditingRelease(r);
													setFormOpen(true);
												}}
												onDelete={confirmDelete}
											/>
										))}
									</Box>
								))}
							</div>
						)}
					</div>
				</CraftModalBody>
				<CraftModalFooter>
					<CraftButton variant="primary" size="m" onClick={onClose}>
						Close
					</CraftButton>
				</CraftModalFooter>
			</CraftModal>

			<ReleaseFormDialog
				open={formOpen}
				onClose={() => {
					setFormOpen(false);
					setEditingRelease(null);
				}}
				onSave={handleSave}
				initialData={editingRelease}
				loading={actionLoading}
			/>

			<Dialog
				open={deleteConfirmOpen}
				onClose={() => setDeleteConfirmOpen(false)}
				maxWidth="xs"
				fullWidth
				PaperProps={{ sx: { borderRadius: "0px" } }}
			>
				<DialogTitle sx={{ fontWeight: 700 }}>Delete Release?</DialogTitle>
				<DialogContent>
					<Typography variant="body2">
						Are you sure you want to delete{" "}
						<strong>{deleteTarget?.title}</strong> ({deleteTarget?.version})?
						This cannot be undone.
					</Typography>
				</DialogContent>
				<DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
					<Button
						onClick={() => setDeleteConfirmOpen(false)}
						variant="outlined"
						color="inherit"
						disabled={actionLoading}
					>
						Cancel
					</Button>
					<Button
						onClick={handleDelete}
						variant="contained"
						color="error"
						disabled={actionLoading}
					>
						{actionLoading ? (
							<CircularProgress size={18} color="inherit" />
						) : (
							"Delete"
						)}
					</Button>
				</DialogActions>
			</Dialog>
		</>
	);
};

export { NewReleasesTickerSection };
export default NewReleasesModal;
